import React, { useEffect, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { listUsersForAssignment } from 'core/users/listUsersForAssignment'

export type RoutineRecipients = {
  userIds: string[]
  emails: string[]
}

type Person = { id: string; label: string; email: string }

function roleLabel(role: string | undefined, isOwner: boolean): string {
  if (isOwner) return 'Owner'
  if (role === 'babyAdmin') return 'Baby admin'
  return 'Admin'
}

export const RoutineRecipientFields: React.FC<{
  value: RoutineRecipients
  onChange: (next: RoutineRecipients) => void
}> = ({ value, onChange }) => {
  const [people, setPeople] = useState<Person[]>([])
  const [outside, setOutside] = useState('')
  const [outsideError, setOutsideError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void listUsersForAssignment()
      .then(users => {
        if (cancelled) return
        setPeople(
          users
            .filter(
              user =>
                user.email.includes('@') &&
                (user.role === 'admin' || user.role === 'babyAdmin' || user.isOwner === true),
            )
            .map(user => ({
              id: user.id,
              email: user.email,
              label: `${user.name || user.email} · ${roleLabel(user.role, user.isOwner === true)}`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        )
      })
      .catch(() => {
        if (!cancelled) setPeople([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const selected = people.filter(person => value.userIds.includes(person.id))

  const addOutside = () => {
    const email = outside.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setOutsideError('Enter a full email address')
      return
    }
    setOutsideError(null)
    setOutside('')
    if (value.emails.includes(email) || value.emails.length >= 20) return
    onChange({ ...value, emails: [...value.emails, email] })
  }

  return (
    <Stack spacing={1}>
      <Typography variant='caption' color='text.secondary'>
        Both email and report routines are sent by email.
      </Typography>
      <Autocomplete
        multiple
        size='small'
        options={people}
        value={selected}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(option, item) => option.id === item.id}
        onChange={(_, next) => onChange({ ...value, userIds: next.slice(0, 20).map(person => person.id) })}
        renderInput={params => <TextField {...params} label='Portal recipients' placeholder='Admin, baby admin, or owner' />}
      />
      <Stack direction='row' spacing={1}>
        <TextField
          size='small'
          fullWidth
          label='Outside email'
          value={outside}
          error={Boolean(outsideError)}
          helperText={outsideError || ' '}
          onChange={event => {
            setOutside(event.target.value)
            setOutsideError(null)
          }}
          onKeyDown={event => {
            if (event.key !== 'Enter') return
            event.preventDefault()
            addOutside()
          }}
        />
        <Button size='small' onClick={addOutside} sx={{ textTransform: 'none', minHeight: 40, alignSelf: 'flex-start' }}>
          Add
        </Button>
      </Stack>
      {value.emails.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {value.emails.map(email => (
            <Chip
              key={email}
              size='small'
              label={email}
              onDelete={() => onChange({ ...value, emails: value.emails.filter(item => item !== email) })}
            />
          ))}
        </Box>
      ) : null}
    </Stack>
  )
}
